<!--
  ABOUT JOVII — the page that has to make somebody want this.

  WHAT CHANGED AND WHY. It opened with `HeroAd`: a trailer in a frame, selling
  a title. The owner: "get rid of the ad, put in the nice Jovii splash screen
  animation that comes up when you open the app, and make that page a lot more
  inspiring. It's supposed to showcase the best of the app."

  So the top is the ident itself (BrandSplash — the first-run animation, rebuilt
  to loop in a box; see that file for why it is not Intro.svelte reused), and
  the page below it is an argument in four moves: what it feels like, what it
  does, what it never keeps, and how to put it on your phone.

  THE PALETTE IS THE BRAND'S. This page used to be #11131a and #eae4dd, which
  predate the rebrand — a blue-grey page wearing a crimson logo. It is Jovii's
  own ink and crimson now. The values are literal rather than tokens on
  purpose: this is a marketing surface with one fixed dark composition, not a
  themed app screen, and it must look identical whatever theme the reader's
  app is set to.

  MOTION IS `use:reveal` AND NOTHING ELSE. One IntersectionObserver per
  element, fires once, and reduced motion skips it entirely — including the
  splash, which falls back to a still lockup.
-->
<script lang="ts">
  import { onMount } from 'svelte';
  import Logo from '$lib/ui/Logo.svelte';
  import Icon from '$lib/ui/Icon.svelte';
  import BrandSplash from '$lib/ui/BrandSplash.svelte';
  import InstallSteps from '$lib/ui/InstallSteps.svelte';
  import { gate } from '$lib/ui/session.svelte';
  import { reveal } from '$lib/ui/reveal';
  import { catalog, posterUrl } from '$lib/ui/cinemeta';
  import { titleHref } from '$lib/ui/nav.svelte';
  import type { CatalogItem } from '$types';

  let movies = $state<CatalogItem[]>([]);
  let shows = $state<CatalogItem[]>([]);
  let loading = $state(true);

  /* The sync demo: two rows that move together when either is pressed. It is
     the one claim on this page that a still picture cannot make. */
  let playing = $state(false);
  let position = $state(17);
  let party = $state(false);
  const stamp = (n: number): string => `00:${String(n).padStart(2, '0')}`;

  onMount(() => {
    const abort = new AbortController();
    void Promise.allSettled([
      catalog({ kind: 'movie', catalog: 'top' }, abort.signal),
      catalog({ kind: 'series', catalog: 'top' }, abort.signal)
    ]).then(([m, s]) => {
      if (abort.signal.aborted) return;
      if (m.status === 'fulfilled') movies = m.value.slice(0, 10);
      if (s.status === 'fulfilled') shows = s.value.slice(0, 10);
      loading = false;
    });
    return () => abort.abort();
  });

  $effect(() => {
    if (!playing) return;
    const t = setInterval(() => {
      if (!document.hidden) position = (position + 1) % 60;
    }, 1000);
    return () => clearInterval(t);
  });
</script>

<svelte:head>
  <title>About Jovii · One more episode?</title>
  <meta
    name="description"
    content="Watch together from anywhere. Jovii keeps the film in sync, keeps your chat and calls private, and installs on your phone like an app."
  />
</svelte:head>

<div class="about">
  <a class="skip" href="#start">Skip to the start</a>

  <header>
    <a href="/" aria-label="Jovii home"><Logo size={30} wordmark={true} /></a>
    <nav aria-label="About navigation">
      <a href="/welcome" aria-current="page">About</a>
      <a href={gate.state === 'open' ? '/profile' : '/signin'}>
        {gate.state === 'open' ? 'Your profile' : 'Sign in'}
      </a>
    </nav>
  </header>

  <main>
    <!-- ── THE IDENT ──────────────────────────────────────────────────────── -->
    <section class="stage" id="start">
      <div class="splash-frame"><BrandSplash /></div>
      <div class="stage-copy">
        <h1>One more episode?<br /><span>Only if it's with you.</span></h1>
        <p>
          A phone, a pillow, and your person at the other end. Jovii makes room for the
          ordinary evenings you miss when you're apart.
        </p>
        <div class="cta-row">
          <a class="cta" href="/">Find something to watch <Icon name="forward" size={18} /></a>
          <a class="cta ghost" href="#install">Put it on your phone</a>
        </div>
      </div>
    </section>

    <!-- ── WHAT IT DOES ───────────────────────────────────────────────────── -->
    <section class="sync" id="sync">
      <div use:reveal>
        <h2>You pause for a snack.<br /><span>The whole room waits.</span></h2>
        <p>
          No counting down over a call. Play, pause and seek together, with Jovii correcting
          the drift while you watch. Stay on a call, keep the chat open, or just enjoy the
          film.
        </p>
      </div>

      <div class="demo" use:reveal={90}>
        <div class="demo-head">
          <strong>Try it</strong>
          <button type="button" aria-pressed={party} onclick={() => (party = !party)}>
            {party ? 'Back to two' : 'Add the friends'} <Icon name="people" size={15} />
          </button>
        </div>
        {#each party ? ['You', 'Sam', 'Ali', 'Mina'] : ['You', 'Sam'] as who (who)}
          <div class="seat">
            <span class="who">{who}</span>
            <div class="track"><div class="fill" style:width="{(position / 60) * 100}%"></div></div>
            <span class="time">{stamp(position)}</span>
          </div>
        {/each}
        <button class="transport" type="button" onclick={() => (playing = !playing)}>
          <Icon name={playing ? 'pause' : 'play'} size={17} />
          {playing ? 'Everyone is watching' : 'Press play for everyone'}
        </button>
      </div>
    </section>

    <!-- ── PRIVACY ────────────────────────────────────────────────────────── -->
    <section class="privacy" id="privacy">
      <div class="privacy-head" use:reveal>
        <h2>What happens in the room<br /><span>stays in the room.</span></h2>
        <p>
          Privacy here is not a setting you have to find. It is how the thing is built, and
          it is the part we would rather be judged on.
        </p>
      </div>
      <div class="cards">
        <div class="card" use:reveal={60}>
          <Icon name="chat" size={22} />
          <h3>Messages are never stored</h3>
          <p>
            Chat is relayed and forgotten in the same breath — validated, passed on, gone.
            There is no transcript on the server to read, leak or hand over, because there
            is no transcript at all.
          </p>
        </div>
        <div class="card" use:reveal={120}>
          <Icon name="speaker" size={22} />
          <h3>Calls go straight to each other</h3>
          <p>
            Video and voice travel peer to peer between your devices. The server helps you
            find each other and then steps out; it never carries the call, so there is
            nothing for it to record.
          </p>
        </div>
        <div class="card" use:reveal={180}>
          <Icon name="people" size={22} />
          <h3>The room disappears with you</h3>
          <p>
            When the last person leaves, the room is gone — no history, no who-watched-what,
            nothing kept after the evening ends.
          </p>
        </div>
      </div>
    </section>

    <!-- ── THE CATALOGUE ──────────────────────────────────────────────────── -->
    <section class="listings" id="tonight">
      <div class="head" use:reveal>
        <h2>So — what are we watching?</h2>
        <a href="/movies">Browse everything <Icon name="forward" size={16} /></a>
      </div>
      <div class="rail" aria-label="Films to explore">
        {#each movies as item (item.id)}
          <a class="poster" href={titleHref(item.kind, item.id)}>
            <img
              src={posterUrl(item.id, 'small')}
              alt=""
              width="300"
              height="450"
              loading="lazy"
              onerror={(e) => e.currentTarget.setAttribute('hidden', '')}
            />
            <strong>{item.name}</strong>
          </a>
        {/each}
        {#if loading}
          {#each Array(8) as _, i (i)}<div class="skeleton" aria-hidden="true"></div>{/each}
        {/if}
      </div>
      <div class="rail" aria-label="Series to explore">
        {#each shows as item (item.id)}
          <a class="poster" href={titleHref(item.kind, item.id)}>
            <img
              src={posterUrl(item.id, 'small')}
              alt=""
              width="300"
              height="450"
              loading="lazy"
              onerror={(e) => e.currentTarget.setAttribute('hidden', '')}
            />
            <strong>{item.name}</strong>
          </a>
        {/each}
      </div>
    </section>

    <!-- ── INSTALL ────────────────────────────────────────────────────────── -->
    <section class="install-section" id="install">
      <div class="install-head" use:reveal>
        <h2>Put Jovii on your phone.<br /><span>It takes about ten seconds.</span></h2>
        <p>
          Jovii is a web app, so there is no app store, no review queue and no update to
          wait for. Add it to your home screen and it behaves like any other app: its own
          icon, full screen, no browser bars — and it is the only way downloads stay put
          and work with no signal.
        </p>
      </div>
      <div use:reveal={80}><InstallSteps /></div>
    </section>

    <!-- ── CLOSE ──────────────────────────────────────────────────────────── -->
    <section class="closing" use:reveal>
      <Logo size={58} wordmark={true} />
      <h2>Meet you here.</h2>
      <p>
        Browse as much as you like. Make an account when you're ready to watch — you'll
        need the invitation you were sent.
      </p>
      <a class="cta" href="/">Back to the films <Icon name="forward" size={18} /></a>
    </section>
  </main>

  <footer>
    <Logo size={24} />
    <a href="/">Browse films and shows</a>
    <span>Made for evenings together.</span>
  </footer>
</div>

<style>
  /* One fixed dark composition — see the header note on why these are literal. */
  .about {
    --ink: #f5f3ed;
    --muted: #c3bab4;
    --faint: #8d817c;
    --crimson: #ef2034;
    --rose: #ff9aa6;
    --ground: #0a0607;
    --panel: #140409;
    --line: rgb(255 255 255 / 0.1);
    background: var(--ground);
    color: var(--ink);
    overflow: clip;
  }

  .skip {
    position: absolute;
    left: -9999px;
  }
  .skip:focus {
    position: static;
    display: inline-block;
    padding: 12px 18px;
    color: var(--ink);
  }

  header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 24px;
    padding: calc(var(--safe-t) + 18px) 5vw 0;
  }
  header > a,
  nav a {
    display: inline-flex;
    align-items: center;
    min-height: 44px;
    color: inherit;
    text-decoration: none;
  }
  nav {
    display: flex;
    gap: 26px;
    font-size: 0.95rem;
    color: var(--muted);
  }
  nav a:hover {
    color: var(--ink);
  }

  h1,
  h2,
  h3 {
    margin: 0;
    font-weight: 560;
    letter-spacing: -0.032em;
    line-height: 1.1;
  }
  h1 {
    font-size: clamp(36px, 4.6vw, 66px);
  }
  h2 {
    font-size: clamp(28px, 3.2vw, 46px);
  }
  h1 span,
  h2 span {
    color: var(--rose);
  }
  p {
    margin: 18px 0 0;
    font-size: 1.02rem;
    line-height: 1.7;
    color: var(--muted);
    max-width: 54ch;
  }

  .cta-row {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 30px;
  }
  .cta {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    min-height: 50px;
    padding: 0 24px;
    border-radius: 999px;
    background: var(--crimson);
    color: #fff;
    font-weight: 640;
    text-decoration: none;
    transition: transform 180ms ease, background-color 180ms ease;
  }
  .cta:hover {
    transform: translateY(-1px);
    background: #ff4a5a;
  }
  .cta.ghost {
    background: transparent;
    border: 1px solid var(--line);
    color: var(--ink);
  }
  .cta.ghost:hover {
    background: rgb(255 255 255 / 0.06);
  }

  section {
    padding: clamp(64px, 8vw, 110px) 5vw;
  }

  /* ── the ident ─────────────────────────────────────────────────────────── */
  .stage {
    display: grid;
    grid-template-columns: 1.05fr 0.95fr;
    gap: 6vw;
    align-items: center;
    padding-top: clamp(30px, 4vw, 60px);
  }
  .splash-frame {
    aspect-ratio: 16 / 10;
    border-radius: 20px;
    overflow: hidden;
    border: 1px solid var(--line);
    box-shadow: 0 40px 90px rgb(0 0 0 / 0.5);
  }

  /* ── sync ──────────────────────────────────────────────────────────────── */
  .sync {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 6vw;
    align-items: center;
    background: var(--panel);
    border-block: 1px solid var(--line);
  }
  .demo {
    padding: 24px;
    border-radius: 16px;
    background: var(--ground);
    border: 1px solid var(--line);
  }
  .demo-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 20px;
  }
  .demo-head button {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-height: 38px;
    padding: 0 14px;
    border: 1px solid var(--line);
    border-radius: 999px;
    background: none;
    color: var(--muted);
    font: inherit;
    font-size: 0.88rem;
    cursor: pointer;
  }
  .seat {
    display: grid;
    grid-template-columns: 54px minmax(0, 1fr) 48px;
    gap: 12px;
    align-items: center;
    margin-bottom: 12px;
    font-size: 0.86rem;
    color: var(--muted);
  }
  .track {
    height: 5px;
    border-radius: 999px;
    background: rgb(255 255 255 / 0.12);
    overflow: hidden;
  }
  .fill {
    height: 100%;
    background: var(--crimson);
    transition: width 900ms linear;
  }
  .time {
    font-variant-numeric: tabular-nums;
    text-align: right;
  }
  .transport {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    min-height: 46px;
    margin-top: 12px;
    padding: 0 20px;
    border: 0;
    border-radius: 999px;
    background: var(--crimson);
    color: #fff;
    font: inherit;
    font-weight: 620;
    cursor: pointer;
  }

  /* ── privacy ───────────────────────────────────────────────────────────── */
  .privacy-head {
    max-width: 62ch;
  }
  .cards {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
    gap: 22px;
    margin-top: 44px;
  }
  .card {
    padding: 26px;
    border-radius: 16px;
    background: var(--panel);
    border: 1px solid var(--line);
    color: var(--rose);
  }
  .card h3 {
    margin-top: 16px;
    font-size: 1.12rem;
    color: var(--ink);
  }
  .card p {
    margin-top: 10px;
    font-size: 0.95rem;
  }

  /* ── listings ──────────────────────────────────────────────────────────── */
  .head {
    display: flex;
    align-items: end;
    justify-content: space-between;
    gap: 24px;
    margin-bottom: 26px;
  }
  .head a {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    min-height: 44px;
    color: var(--muted);
    text-decoration: none;
    white-space: nowrap;
  }
  .rail {
    display: grid;
    grid-auto-flow: column;
    grid-auto-columns: clamp(132px, 14vw, 186px);
    gap: 16px;
    overflow-x: auto;
    padding-bottom: 22px;
    scrollbar-width: none;
  }
  .rail::-webkit-scrollbar {
    display: none;
  }
  .poster {
    color: inherit;
    text-decoration: none;
  }
  .poster img {
    width: 100%;
    height: auto;
    aspect-ratio: 2 / 3;
    object-fit: cover;
    border-radius: 12px;
    background: var(--panel);
    transition: transform 220ms ease;
  }
  .poster:hover img {
    transform: translateY(-4px);
  }
  .poster strong {
    display: block;
    margin-top: 10px;
    font-size: 0.88rem;
    font-weight: 560;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  .skeleton {
    aspect-ratio: 2 / 3;
    border-radius: 12px;
    background: var(--panel);
  }

  /* ── install ───────────────────────────────────────────────────────────── */
  .install-section {
    background: var(--panel);
    border-block: 1px solid var(--line);
  }
  .install-head {
    max-width: 62ch;
    margin-bottom: 46px;
  }

  /* ── close ─────────────────────────────────────────────────────────────── */
  .closing {
    display: grid;
    justify-items: center;
    text-align: center;
    gap: 18px;
  }
  .closing p {
    margin: 0;
  }

  footer {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 20px;
    padding: 34px 5vw calc(34px + var(--safe-b));
    border-top: 1px solid var(--line);
    color: var(--faint);
    font-size: 0.88rem;
  }
  footer a {
    color: var(--muted);
  }

  /* ── reveal (lib/ui/reveal.ts sets the attribute) ──────────────────────── */
  :global([data-reveal='hidden']) {
    opacity: 0;
    transform: translateY(22px);
  }
  :global([data-reveal='shown']) {
    opacity: 1;
    transform: none;
    transition:
      opacity 620ms cubic-bezier(0.22, 1, 0.36, 1) var(--reveal-delay, 0ms),
      transform 620ms cubic-bezier(0.22, 1, 0.36, 1) var(--reveal-delay, 0ms);
  }

  @media (max-width: 900px) {
    .stage,
    .sync {
      grid-template-columns: 1fr;
      gap: 36px;
    }
    .cards {
      grid-template-columns: 1fr;
    }
    .head {
      flex-direction: column;
      align-items: start;
      gap: 10px;
    }
  }
</style>